import { APP_CHAIN } from '@/client/confs/app';
import { watchTransaction } from '@/client/hooks/use-transaction-recovery';
import { useWallet } from '@/client/hooks/use-wallet';
import { topmostOwner, useTradeOverlayStore } from '@/client/store/trade-overlay-store';
import {
  canAcknowledgeWalletCancellation,
  dismissKey,
  isActiveTransaction,
  type TxRecord,
  useTransactionStore,
} from '@/client/store/transaction-store';
import { useTranslation } from 'react-i18next';
import useMedia from 'react-use/lib/useMedia';

/**
 * The transaction the global toast shows for the connected account on APP_CHAIN,
 * plus the two recovery actions it can offer. Shared by the toast and the trade
 * panel (which hosts them on mobile while a trade overlay hides the toast), so
 * the conditions and handlers live in exactly one place.
 */
export const useTransactionActions = () => {
  const { t } = useTranslation();
  const { walletClient } = useWallet();
  const account = walletClient?.account?.address;
  const record = useTransactionStore(state => {
    if (!account || walletClient?.chain?.id !== APP_CHAIN.id) return undefined;
    // Dismissing a finished result hides it and every older finished result, so
    // the previous trade never takes its place. Active transactions always show.
    const dismissedThrough = state.dismissedThrough[dismissKey(APP_CHAIN.id, account)] ?? -Infinity;
    return (
      state.findActive(APP_CHAIN.id, account) ??
      Object.values(state.records)
        .filter(
          item =>
            item.chainId === APP_CHAIN.id &&
            item.account.toLowerCase() === account.toLowerCase() &&
            item.startedAt > dismissedThrough
        )
        .sort((a, b) => b.startedAt - a.startedAt)[0]
    );
  });
  /** A broadcast transaction still unresolved: re-query its receipt. */
  const canRecheck = !!record?.hash && isActiveTransaction(record);
  /** A restored request with no hash: the user may confirm they cancelled it in the wallet. */
  const canAcknowledgeCancel = !!record && canAcknowledgeWalletCancellation(record);
  const recheck = () => {
    if (!record || !canRecheck) return;
    void watchTransaction(record.id, undefined, true).catch(() => {});
  };
  const acknowledgeCancel = () => {
    if (!record || !canAcknowledgeCancel) return;
    if (window.confirm(t('transaction-wallet-cancelled-confirm')))
      useTransactionStore.getState().update(record.id, { stage: 'cancelled' });
  };
  return { record, canRecheck, canAcknowledgeCancel, recheck, acknowledgeCancel };
};

/**
 * True while the global toast hides `record`: mobile with the topmost trade
 * overlay hosting the record's market (see store/trade-overlay-store). That
 * overlay shows the toast's actions exactly then, so each action is on screen
 * once at most. Any other case (other market, or the record's overlay covered
 * by another market's overlay) keeps the toast.
 */
export const useTransactionToastHidden = (record: TxRecord | undefined) => {
  const isDesktop = useMedia('(min-width: 768px)');
  const topmostHostsRecord = useTradeOverlayStore(state => topmostOwner(state.slots, record?.marketAddress) !== undefined);
  return !isDesktop && topmostHostsRecord;
};
